import { motion } from "framer-motion";
import { Palette, Package, ShieldCheck, Rocket } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import Seo from "@/components/common/Seo";
import Reveal from "@/components/common/Reveal";
import MultiStepForm from "@/components/forms/MultiStepForm";
import { stagger, fadeUp } from "@/lib/motion";

const BENEFITS = [
  { icon: Palette, title: "Your Brand, Your Identity", text: "Custom labels, packaging and formulations." },
  { icon: Package, title: "Proven Formulations", text: "Launch fast with ready or custom recipes." },
  { icon: ShieldCheck, title: "Quality Assured", text: "Rigorously tested, compliant products." },
  { icon: Rocket, title: "Faster Go-To-Market", text: "From idea to shelf with our expertise." },
];

const STEPS = [
  {
    title: "Basic Information",
    subtitle: "Tell us who you are.",
    fields: [
      { name: "fullName", label: "Full Name", type: "text", required: true, placeholder: "Your full name" },
      { name: "companyName", label: "Company / Business Name", type: "text", placeholder: "Business name" },
      { name: "mobile", label: "Mobile Number", type: "tel", required: true, placeholder: "+91" },
      { name: "email", label: "Email Address", type: "email", required: true, placeholder: "you@email.com" },
      { name: "cityState", label: "City & State", type: "text", placeholder: "City, State" },
      { name: "website", label: "Website / Social Media", type: "text", placeholder: "https://" },
    ],
  },
  {
    title: "Brand Vision & Positioning",
    subtitle: "Where your brand stands today.",
    fields: [
      { name: "existingBrand", label: "Do you currently have an existing brand?", type: "radio", options: ["Yes", "No"] },
      { name: "stage", label: "What stage are you at?", type: "radio", full: true, options: ["Idea stage", "Early-stage brand", "Established brand"] },
      { name: "market", label: "Which market are you targeting?", type: "radio", full: true, options: ["Mass / Budget", "Mid-range", "Premium / Professional"] },
      { name: "targetAudience", label: "Primary target audience", type: "checkbox", full: true, options: ["Workshops / Service Centers (B2B)", "Car Detailers", "Retail Consumers (B2C)", "E-commerce"] },
      { name: "differentiator", label: "What differentiates your brand?", type: "textarea", full: true, placeholder: "Your unique positioning..." },
    ],
  },
  {
    title: "Product Requirements",
    subtitle: "What you want to launch.",
    fields: [
      { name: "productCategories", label: "Product categories of interest", type: "checkbox", full: true, options: ["Polishes & compounds", "Aerosols", "Car cleaning & detailing products"] },
      { name: "formulationType", label: "Do you need", type: "radio", options: ["Ready formulations", "Custom formulations"] },
      { name: "skuCount", label: "Estimated SKUs to launch initially", type: "text", placeholder: "e.g. 5" },
      { name: "packagingType", label: "Preferred packaging type", type: "radio", full: true, options: ["Bottles", "Spray cans", "Combo kits"] },
      { name: "customSpecs", label: "Need custom fragrance / finish / performance specs?", type: "radio", options: ["Yes", "No"] },
    ],
  },
  {
    title: "Volume & Commercials",
    subtitle: "Scale and budget.",
    fields: [
      { name: "initialMOQ", label: "Estimated initial order quantity (MOQ)", type: "radio", full: true, options: ["< 500 units", "500 - 2,000 units", "2,000 - 5,000 units", "5,000+ units"] },
      { name: "monthlyVolume", label: "Expected monthly volume after scale", type: "select", options: ["< 1,000 units", "1,000 - 5,000 units", "5,000 - 20,000 units", "20,000+ units"] },
      { name: "budgetPerSku", label: "Budget per SKU (approx)", type: "text", placeholder: "e.g. ₹50,000" },
      { name: "timeline", label: "Timeline to launch", type: "radio", full: true, options: ["Immediate", "1-3 months", "3-6 months", "6+ months"] },
    ],
  },
  {
    title: "Branding & Packaging Readiness",
    subtitle: "What you already have.",
    fields: [
      { name: "haveAssets", label: "Do you already have", type: "checkbox", full: true, options: ["Logo", "Packaging design", "Brand guidelines"] },
      { name: "needSupport", label: "Do you need support with", type: "checkbox", full: true, options: ["Packaging design", "Label compliance", "Branding strategy"] },
      { name: "printReady", label: "Will you provide print-ready artwork?", type: "radio", options: ["Yes", "No"] },
    ],
  },
  {
    title: "Sales & Distribution Strategy",
    subtitle: "Your go-to-market plan.",
    fields: [
      { name: "salesChannels", label: "How do you plan to sell your brand?", type: "checkbox", full: true, options: ["Offline distribution", "Online marketplaces", "D2C website", "Workshops / dealer network"] },
      { name: "channelMix", label: "Expected sales channel mix (optional)", type: "text", full: true, placeholder: "e.g. 60% offline, 40% online" },
      { name: "existingNetwork", label: "Do you have an existing sales network?", type: "radio", options: ["Yes", "No"] },
    ],
  },
  {
    title: "Compliance & Business Credentials",
    subtitle: "Business verification.",
    fields: [
      { name: "registeredEntity", label: "Do you have a registered business entity?", type: "radio", options: ["Yes", "No"] },
      { name: "gst", label: "GST Number", type: "text", placeholder: "GSTIN" },
      { name: "certifications", label: "Required certifications (if applicable)", type: "text", placeholder: "e.g. ISO, BIS" },
      { name: "document", label: "Upload business documents", type: "file", full: true },
    ],
  },
  {
    title: "Operational Alignment",
    subtitle: "Execution readiness.",
    fields: [
      { name: "moqComfort", label: "Comfortable with minimum order quantities (MOQ)?", type: "radio", options: ["Yes", "No"] },
      { name: "standardFormulations", label: "Open to standardized formulations initially?", type: "radio", options: ["Yes", "No"] },
      { name: "logisticsPref", label: "Logistics preference", type: "radio", options: ["Self-managed", "Require support"] },
    ],
  },
  {
    title: "Final Section",
    subtitle: "Almost there.",
    fields: [
      { name: "whyBuildBrand", label: "Why do you want to build your own automotive brand?", type: "textarea", full: true, placeholder: "Tell us your vision..." },
      { name: "notes", label: "Additional requirements or notes", type: "textarea", full: true, placeholder: "Anything else?" },
      { name: "callbackTime", label: "Preferred time for a callback", type: "select", options: ["Morning (9am - 12pm)", "Afternoon (12pm - 4pm)", "Evening (4pm - 8pm)"] },
    ],
  },
];

export default function WhiteLabel() {
  const handleSubmit = async (values, file) => {
    const fd = new FormData();
    const payload = { ...values };
    delete payload.document;
    fd.append("payload", JSON.stringify(payload));
    if (file) fd.append("document", file);
    const res = await api.createWhiteLabelLead(fd);
    toast.success(res.message || "Application submitted");
  };

  return (
    <div className="bg-[var(--acx-bg)]">
      <Seo title="White Label" description="Build your own automotive care brand with AutoCareX white label and private labeling services." />

      <section className="relative acx-noise overflow-hidden border-b border-[color:var(--acx-border)]">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(800px_circle_at_80%_0%,rgba(156,163,175,0.12),transparent_55%)]" />
        <div className="acx-container relative px-4 py-20 sm:px-6 lg:px-10 lg:py-28">
          <Reveal>
            <span className="text-[12px] uppercase tracking-[0.22em] text-white/50">Private Labeling</span>
            <h1 className="mt-3 max-w-3xl font-display text-[40px] font-extrabold leading-[1.04] tracking-[-0.03em] sm:text-[56px]">
              White label our products in your brand name.
            </h1>
            <p className="mt-5 max-w-xl text-[17px] leading-relaxed text-white/65">
              Launch your own premium automotive care line with our proven formulations, manufacturing
              expertise and end-to-end branding support.
            </p>
          </Reveal>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={stagger} className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {BENEFITS.map((b) => (
              <motion.div key={b.title} variants={fadeUp} className="rounded-[16px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-6">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#9CA3AF]/15 text-[#9CA3AF]"><b.icon size={20} /></span>
                <p className="mt-4 font-display text-[16px] font-semibold text-white">{b.title}</p>
                <p className="mt-1.5 text-[13px] leading-relaxed text-white/55">{b.text}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="bg-[var(--acx-bg-2)]">
        <div className="acx-container px-4 py-16 sm:px-6 lg:px-10">
          <div className="mx-auto max-w-3xl">
            <Reveal className="mb-8 text-center">
              <h2 className="font-display text-3xl font-bold tracking-[-0.02em] sm:text-4xl">White Label Application</h2>
              <p className="mt-3 text-[15px] text-white/55">A detailed qualification so we can tailor the right solution for you.</p>
            </Reveal>
            <MultiStepForm steps={STEPS} onSubmit={handleSubmit} submitLabel="Submit White Label Application" testId="white-label-multistep-form" accent="#E11B22" />
          </div>
        </div>
      </section>
    </div>
  );
}
